/**
 * Buy-again widget API client.
 * Uses customer ID from window.meta.page.customerId when available; otherwise
 * uses customer email from localStorage (set by thank-you page).
 * Calls Next.js proxy (/api/widget/buy-again/...) which adds X-Service-Secret
 * and forwards to the backend.
 */

import { getStoredRawValue } from "./emailStorage";

const API_BASE =
  import.meta.env.VITE_PUBLIC_SOCIAL_OCEAN_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  "";
const PROXY_PREFIX = "/api/widget/buy-again";

function getCustomerId(): number | null {
  if (typeof window === "undefined") return null;
  const id = (
    window as unknown as { meta?: { page?: { customerId?: number } } }
  )?.meta?.page?.customerId;
  return id != null && Number.isFinite(id) ? id : null;
}

export function getCustomerIdOrThrow(): number {
  const id = getCustomerId();
  if (id == null) {
    throw new Error("Customer ID not available (window.meta.page.customerId)");
  }
  return id;
}

/** True if we have customer ID or stored (encoded) email. */
export function hasCustomerId(): boolean {
  return getCustomerId() != null || getStoredRawValue() != null;
}

export type CustomerIdentifier =
  | { type: "id"; shopify_customer_id: number }
  | { type: "encoded_email"; encoded_customer_email: string };

/** Resolve customer by ID first, then by encoded email from storage (server decodes). */
export function getCustomerIdentifier(): CustomerIdentifier | null {
  const id = getCustomerId();
  if (id != null) return { type: "id", shopify_customer_id: id };
  const encoded = getStoredRawValue();
  if (encoded) return { type: "encoded_email", encoded_customer_email: encoded };
  return null;
}

async function backendFetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const base = API_BASE.replace(/\/$/, "");
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options?.headers as Record<string, string>),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      (data as { message?: string })?.message ||
      (data as { error?: { message?: string } })?.error?.message ||
      `Request failed: ${path}`;
    const err = new Error(msg) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return (data?.data !== undefined ? data.data : data) as T;
}

function buildQueryParams(identifier: CustomerIdentifier): string {
  if (identifier.type === "id") {
    return `shopify_customer_id=${encodeURIComponent(identifier.shopify_customer_id)}`;
  }
  return `encoded_customer_email=${encodeURIComponent(identifier.encoded_customer_email)}`;
}

/** GET proxy → backend purchased-products list */
export async function fetchPurchasedProducts(): Promise<unknown[]> {
  const identifier = getCustomerIdentifier();
  if (!identifier) {
    throw new Error(
      "Customer identifier not available (customer ID or email from storage)",
    );
  }
  const path = `${PROXY_PREFIX}/purchased-products?${buildQueryParams(identifier)}`;
  const result = await backendFetch<unknown[]>(path, { cache: "no-store" });
  return Array.isArray(result) ? result : [];
}

/** GET proxy → backend purchased-product detail */
export async function fetchPurchasedProductDetail(
  productId: number,
): Promise<unknown | null> {
  const identifier = getCustomerIdentifier();
  if (!identifier) {
    throw new Error(
      "Customer identifier not available (customer ID or email from storage)",
    );
  }
  const path = `${PROXY_PREFIX}/purchased-products/${productId}?${buildQueryParams(identifier)}`;
  const result = await backendFetch<unknown | null>(path, {
    cache: "no-store",
  });
  return result ?? null;
}
